export const privacyContent = {
  en: {
    meta_title: "Privacy & Cookies | Khalfan Oman",
    meta_description: "Learn how Khalfan Oman handles personal information, analytics cookies, Google Analytics, and links to services such as WhatsApp and Instagram.",
    overline: "Privacy",
    title: "Privacy & Cookies",
    summary: "A clear overview of how khalfanoman.com handles information, uses consent-based analytics, and remembers your cookie choice.",
    last_updated: "Last updated: September 2026",
    introduction: {
      title: "Introduction",
      body: "Khalfan Oman respects the privacy of visitors to khalfanoman.com. This page explains what information may be collected when you use the website, why it is used, and how you can control analytics cookies.",
    },
    controller: {
      title: "Data Controller",
      statement: "Khalfan Said Al Saadi is the data controller for this website.",
      name: "Khalfan Said Al Saadi",
      role: "Independent licensed tour guide",
      location: "Muscat, Sultanate of Oman",
      email_label: "Email",
      email: "khalfan@khalfanoman.com",
    },
    information: {
      title: "Information collected",
      account: "The website does not require visitors to create an account.",
      forms: "If you voluntarily send a booking or contact request, the website processes the details you provide so Khalfan can respond. These may include your name, email address, WhatsApp number, selected tour and date, number of travelers, and an optional message.",
      analytics_intro: "Depending on your choices and interactions, technical and analytics information may include:",
      analytics_items: [
        "pages viewed",
        "interactions with the website",
        "approximate technical or device information made available through analytics",
        "clicks on specific calls to action",
      ],
    },
    analytics: {
      title: "Google Analytics",
      intro: "The website uses Google Analytics 4 (GA4), provided by Google, only after you have explicitly accepted analytics cookies.",
      purpose_intro: "Google Analytics is used to:",
      purposes: [
        "measure website usage",
        "understand which content is useful",
        "understand interactions with the website",
        "improve the website and visitor experience",
      ],
      advertising: "Advertising cookies are not used by the current implementation.",
      retention_intro: "The following retention settings are currently configured in Google Analytics:",
      retention: [
        { label: "Event data associated with identifiers", value: "2 months" },
        { label: "User data", value: "14 months" },
      ],
      retention_note: "These are the retention settings configured in Google Analytics. Aggregated or reporting data may be handled differently and is not necessarily deleted after exactly these periods.",
    },
    whatsapp: {
      title: "WhatsApp interaction measurement",
      intro: "After analytics consent has been granted, the website records an analytics event when you click one of its WhatsApp calls to action. This is used to understand which area of the website generated the interaction, such as where the WhatsApp button was clicked.",
      limits: [
        "Khalfan Oman analytics does not record the content of WhatsApp conversations.",
        "The analytics event does not send your WhatsApp message to Google Analytics.",
        "Clicking WhatsApp transfers you to an external service operated by WhatsApp/Meta, whose own privacy terms then apply.",
      ],
    },
    external_links: {
      title: "Instagram and external links",
      body: "The website may contain links to third-party services such as Instagram and WhatsApp. When you follow these links, you leave khalfanoman.com and the privacy practices of the third-party service apply.",
    },
    cookies: {
      title: "Cookies and consent",
      intro: "Analytics remains disabled until you accept analytics cookies. You can accept analytics, reject analytics, or change your decision later. The current implementation does not use advertising cookies.",
      storage: "Your consent preference is stored locally on your device so the website can remember your choice. This preference controls the website's analytics implementation; third-party services apply their own practices when you choose to follow an external link.",
    },
    withdrawal: {
      title: "Withdrawing consent",
      body_before: "You can withdraw or change your analytics consent at any time using the",
      control: "Cookie preferences",
      body_after: "control in the website footer. This opens the existing consent preferences interface.",
    },
    legal_basis: {
      title: "Legal basis",
      body: "For visitors covered by GDPR or ePrivacy rules, analytics processing is based on consent.",
    },
    sharing: {
      title: "Data sharing and service providers",
      body: "Analytics data may be processed by Google as the provider of Google Analytics. Third-party services may process data under their own privacy terms when you choose to interact with them.",
    },
    rights: {
      title: "Visitor rights",
      intro: "Where applicable under data protection law, and depending on the circumstances, you may have rights concerning your personal data, including:",
      items: [
        "access",
        "rectification",
        "deletion",
        "restriction",
        "objection, where applicable",
        "withdrawal of consent",
      ],
      note: "Not every right applies unconditionally in every jurisdiction or situation.",
      contact_before: "For questions or requests, contact",
      email: "khalfan@khalfanoman.com",
    },
    updates: {
      title: "Policy updates",
      body: "This policy may be updated if the website, analytics configuration, or applicable requirements change.",
    },
  },
  fr: {
    meta_title: "Confidentialité et cookies | Khalfan Oman",
    meta_description: "Découvrez comment Khalfan Oman traite les informations personnelles, les cookies d’analyse, Google Analytics et les liens vers WhatsApp et Instagram.",
    overline: "Confidentialité",
    title: "Confidentialité et cookies",
    summary: "Une présentation claire de la manière dont khalfanoman.com traite les informations, utilise des statistiques soumises au consentement et mémorise votre choix de cookies.",
    last_updated: "Dernière mise à jour : septembre 2026",
    introduction: {
      title: "Introduction",
      body: "Khalfan Oman respecte la vie privée des visiteurs de khalfanoman.com. Cette page explique quelles informations peuvent être collectées lorsque vous utilisez le site, pourquoi elles sont utilisées et comment vous pouvez contrôler les cookies d’analyse.",
    },
    controller: {
      title: "Responsable du traitement",
      statement: "Khalfan Said Al Saadi est le responsable du traitement pour ce site.",
      name: "Khalfan Said Al Saadi",
      role: "Guide touristique indépendant agréé",
      location: "Mascate, Sultanat d’Oman",
      email_label: "E-mail",
      email: "khalfan@khalfanoman.com",
    },
    information: {
      title: "Informations collectées",
      account: "Le site ne vous oblige pas à créer un compte.",
      forms: "Si vous envoyez volontairement une demande de réservation ou de contact, le site traite les informations que vous fournissez afin que Khalfan puisse vous répondre. Elles peuvent inclure votre nom, votre adresse e-mail, votre numéro WhatsApp, le voyage et la date choisis, le nombre de voyageurs et un message facultatif.",
      analytics_intro: "Selon vos choix et vos interactions, les informations techniques et statistiques peuvent inclure :",
      analytics_items: [
        "les pages consultées",
        "les interactions avec le site",
        "des informations techniques ou relatives à l’appareil, approximatives et mises à disposition par l’outil d’analyse",
        "les clics sur certains appels à l’action",
      ],
    },
    analytics: {
      title: "Google Analytics",
      intro: "Le site utilise Google Analytics 4 (GA4), fourni par Google, uniquement après votre acceptation explicite des cookies d’analyse.",
      purpose_intro: "Google Analytics est utilisé pour :",
      purposes: [
        "mesurer l’utilisation du site",
        "comprendre quels contenus sont utiles",
        "comprendre les interactions avec le site",
        "améliorer le site et l’expérience des visiteurs",
      ],
      advertising: "Les cookies publicitaires ne sont pas utilisés dans l’implémentation actuelle.",
      retention_intro: "Les paramètres de conservation suivants sont actuellement configurés dans Google Analytics :",
      retention: [
        { label: "Données d’événements associées à des identifiants", value: "2 mois" },
        { label: "Données utilisateur", value: "14 mois" },
      ],
      retention_note: "Il s’agit des paramètres de conservation configurés dans Google Analytics. Les données agrégées ou intégrées à des rapports peuvent être gérées différemment et ne sont pas nécessairement supprimées exactement à l’issue de ces périodes.",
    },
    whatsapp: {
      title: "Mesure des interactions WhatsApp",
      intro: "Après votre consentement aux statistiques, le site enregistre un événement d’analyse lorsque vous cliquez sur l’un de ses appels à l’action WhatsApp. Cet événement sert à comprendre quelle zone du site a généré l’interaction, par exemple l’endroit où le bouton WhatsApp a été sélectionné.",
      limits: [
        "Les statistiques de Khalfan Oman n’enregistrent pas le contenu des conversations WhatsApp.",
        "L’événement d’analyse n’envoie pas votre message WhatsApp à Google Analytics.",
        "Un clic sur WhatsApp vous transfère vers un service externe exploité par WhatsApp/Meta, dont les propres conditions de confidentialité s’appliquent alors.",
      ],
    },
    external_links: {
      title: "Instagram et liens externes",
      body: "Le site peut contenir des liens vers des services tiers tels qu’Instagram et WhatsApp. Lorsque vous suivez ces liens, vous quittez khalfanoman.com et les pratiques de confidentialité du service tiers s’appliquent.",
    },
    cookies: {
      title: "Cookies et consentement",
      intro: "Les statistiques restent désactivées tant que vous n’avez pas accepté les cookies d’analyse. Vous pouvez accepter les statistiques, les refuser ou modifier votre décision ultérieurement. L’implémentation actuelle n’utilise pas de cookies publicitaires.",
      storage: "Votre préférence de consentement est enregistrée localement sur votre appareil afin que le site puisse mémoriser votre choix. Cette préférence contrôle l’implémentation des statistiques du site ; les services tiers appliquent leurs propres pratiques lorsque vous choisissez de suivre un lien externe.",
    },
    withdrawal: {
      title: "Retrait du consentement",
      body_before: "Vous pouvez retirer ou modifier votre consentement aux statistiques à tout moment grâce au contrôle",
      control: "Préférences des cookies",
      body_after: "présent dans le pied de page. Celui-ci ouvre l’interface existante de gestion du consentement.",
    },
    legal_basis: {
      title: "Base juridique",
      body: "Pour les visiteurs auxquels les règles du RGPD ou ePrivacy s’appliquent, le traitement à des fins statistiques repose sur le consentement.",
    },
    sharing: {
      title: "Partage des données et prestataires",
      body: "Les données statistiques peuvent être traitées par Google en sa qualité de fournisseur de Google Analytics. Les services tiers peuvent traiter des données selon leurs propres conditions de confidentialité lorsque vous choisissez d’interagir avec eux.",
    },
    rights: {
      title: "Droits des visiteurs",
      intro: "Lorsque la législation sur la protection des données s’applique, et selon les circonstances, vous pouvez disposer de droits concernant vos données personnelles, notamment :",
      items: [
        "l’accès",
        "la rectification",
        "la suppression",
        "la limitation",
        "l’opposition, lorsqu’elle s’applique",
        "le retrait du consentement",
      ],
      note: "Tous ces droits ne s’appliquent pas nécessairement de manière inconditionnelle dans chaque juridiction ou situation.",
      contact_before: "Pour toute question ou demande, contactez",
      email: "khalfan@khalfanoman.com",
    },
    updates: {
      title: "Mises à jour de la politique",
      body: "Cette politique peut être mise à jour si le site, la configuration des statistiques ou les exigences applicables évoluent.",
    },
  },
};
